import { fg } from '@atlaskit/platform-feature-flags';

import type { ViewContext } from '../../custom-ui/iframe/types';
import { ForgeAppError } from '../ForgeAppError';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

type GetContextContext = MakeBridgeContext<'viewContext'>;

/**
 * Returns the current view context to the Forge app running inside the Forge UI runtime.
 *
 * The view context is a read-only snapshot assembled from:
 * - Core site/environment data (`cloudId`, `siteUrl`, `moduleKey`, `localId`, etc.)
 * - Extension-specific static data (`extensionData`) and dynamic data (`extensionPayload`)
 * - View-only data never sent to the backend (`extensionViewData`, e.g. macro body ADF)
 * - User data (`accountId`, `locale`, `timezone`, `license`, `theme`)
 * - Egress and scope permissions
 *
 * This is a pure in-memory read — no network call is made. The context is
 * memoized in the host and recomputed only when its inputs change.
 *
 * @see getViewContext — host-side assembly of this object
 * @see ViewContext — full shape of the returned object
 */
export const getContext: TBridgeMethod<GetContextContext, void, ViewContext> = async ({
	viewContext,
}) => {
	return viewContext;
};

type InvokeData = {
	functionKey: string;
	payload: object;
};

type InvokeContext = MakeBridgeContext<
	'resolverContext' | 'extensionPayload' | 'contextIds',
	'invoker' | 'getContextToken' | 'tracing' | 'onThreeLO'
>;

/**
 * Calls a backend resolver function on behalf of the Forge app running inside the iframe.
 *
 * @param data.functionKey - The resolver function identifier declared in the app manifest.
 * @param data.payload - Arbitrary arguments object passed to the resolver function.
 *
 * Flow:
 * 1. Fetches a fresh Forge Context Token (FCT) via `getContextToken`.
 * 2. Invokes the `invokeExtension` GraphQL mutation with the resolver call,
 *    the current `resolverContext` (site/extension identity data), and the
 *    optional `extensionPayload` (dynamic per-call data included in the
 *    Forge Invocation Token but excluded from the FCT claims).
 * 3. On `USER_CONSENT_REQUIRED` (3LO): the backend could not proceed because
 *    the user has not granted OAuth consent for this app. Delegates to
 *    `services.onThreeLO`, which shows the consent prompt and receives an
 *    `authUrl` to redirect the user to. After consent, retries the invocation
 *    automatically.
 * 4. On any other failure, throws so the bridge propagates the error back to
 *    the app. Failures that carry a backend `errorType` are thrown as a
 *    {@link ForgeAppError} so the `withErrorDisplay` middleware can decide
 *    whether to surface a Forge UI runtime error message — display is NOT this method's
 *    concern (keeps the bridge layer React-free).
 * 5. On success, returns the resolver response body.
 */
export const invoke: TBridgeMethod<InvokeContext, InvokeData, object> = async (
	{
		services: { invoker, getContextToken, tracing, onThreeLO },
		resolverContext,
		extensionPayload,
		contextIds,
	},
	data,
) => {
	const doInvoke = async (): Promise<object> => {
		const contextToken = await getContextToken({ contextIdsOverride: contextIds });

		tracing.recordGqlCall('invokeExtension - start');

		const { data: gqlData } = await invoker({
			call: data,
			context: { ...resolverContext },
			extensionPayload,
			contextToken,
		});

		const resp = gqlData?.invokeExtension;

		if (!resp?.success) {
			const error = resp?.errors?.[0];

			if (error?.extensions.errorType === 'USER_CONSENT_REQUIRED') {
				const authUrl = error?.extensions.fields?.authInfoUrl as string;
				return new Promise<object>((resolve, reject) => {
					onThreeLO(authUrl, async () => {
						try {
							resolve(await doInvoke());
						} catch (e) {
							reject(e);
						}
					}).catch(reject);
				});
			}

			const message = error?.message ?? 'An error occurred while trying to invoke.';
			// Carry the errorType so the withErrorDisplay middleware can classify and
			// (for display-worthy types) surface an error message. Still throws either way.
			if (error?.extensions.errorType) {
				throw new ForgeAppError(error.extensions.errorType, message);
			}
			throw new Error(message);
		}

		tracing.recordGqlCall('invokeExtension - end');

		if (fg('forge-ui-return-rate-limit-properties')) {
			return {
				body: resp.response?.body ?? {},
				metadata: {
					rateLimitProperties: resp.rateLimitProperties ?? null,
				},
			};
		}

		return resp.response?.body ?? {};
	};

	return doInvoke();
};

type GetFrameIdContext = MakeBridgeContext<'extension'>;

/**
 * Returns the `frameId` declared in the extension manifest's `properties`,
 * or `null` if none is set.
 *
 * The `frameId` is a stable, product-provided string used to correlate a
 * specific iframe instance — e.g. for FAUS navigation listeners that key
 * on `navigateFromEmbeddedFAUS_<localId>`. Returning `null` rather than
 * `undefined` keeps the wire format consistent across runtimes.
 */
export const getFrameId: TBridgeMethod<GetFrameIdContext, void, string | null> = async ({
	extension,
}) => {
	return extension.properties?.frameId ?? null;
};

/**
 * Reloads the host page by calling `window.location.reload()`.
 *
 * Accepts no context fields and no payload — it is a pure side-effect method.
 */
export const reload: TBridgeMethod<MakeBridgeContext, void, void> = async () => {
	window.location.reload();
};
